import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { formatCount } from "@admin-ui/utils/format-count";
import { Head, router } from "@inertiajs/react";
import { adminLocationsPath, editAdminDestinationPath, newAdminDestinationPath } from "@travily/routes";
import type AdminLocationsIndex from "@travily/types/pages/AdminLocationsIndex";
import { FilterLines } from "@untitledui/icons";
import type { ReactNode } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminListNavigation } from "@/hooks/admin/use-admin-list-navigation";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { IndexView } from "./index.view";

type AdminLocationsIndexPageProps = AdminLocationsIndex;

function AdminLocationsIndexPage({
  locations,
  pagy,
  sort,
  sort_dir,
  without_destination,
}: AdminLocationsIndexPageProps) {
  const account = useAdminLayoutAccount();

  const { navigate, handlePageChange, handleSortChange } = useAdminListNavigation({
    basePath: adminLocationsPath(),
    filters: { without_destination: without_destination ? "true" : "" },
    sort,
    sortDir: sort_dir,
    filterToRansack: { without_destination: "without_destination" },
  });

  const handleFilterToggle = () =>
    navigate({ filters: { without_destination: without_destination ? "" : "true" }, page: 1 });

  const handleCreateDestination = (locationId: number) => {
    router.get(newAdminDestinationPath(), { from_location_id: locationId });
  };

  const handleViewDestination = (destinationId: number) => {
    router.get(editAdminDestinationPath(destinationId));
  };

  const locationCount = formatCount(pagy.count, "location");

  return (
    <AdminLayout activeUrl={adminLocationsPath()} account={account}>
      <Head title="Locations - Admin" />

      <AdminPageHeader
        breadcrumbs={[]}
        title="Locations"
        titleBadge={
          <Badge color="gray" size="sm">
            {locationCount}
          </Badge>
        }
        subtitle="All locations referenced by trips"
        actions={
          <Button
            size="sm"
            color={without_destination ? "primary" : "secondary"}
            iconLeading={FilterLines}
            aria-pressed={without_destination}
            onClick={handleFilterToggle}
          >
            Without destination
          </Button>
        }
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-6 px-4 lg:gap-8 lg:px-8">
        <IndexView
          locations={locations}
          pagy={pagy}
          sort={sort}
          sortDir={sort_dir}
          withoutDestination={without_destination}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onCreateDestination={handleCreateDestination}
          onViewDestination={handleViewDestination}
        />
      </div>
    </AdminLayout>
  );
}

AdminLocationsIndexPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminLocationsIndexPage;
